import React from 'react';
import { Line } from 'react-chartjs-2';
import 'chart.js/auto';
import 'chartjs-adapter-date-fns';
import { baseOptions, timeAxis, valueAxis } from '../../utils/chartConfig';

const SolarChart = ({ solaxData, solarForecast, selectedDate }) => {
    const historical = solaxData || [];
    const forecast = solarForecast || [];
    if (historical.length === 0 && forecast.length === 0) {
        return (
            <div className="h-full flex flex-col items-center justify-center text-gray-400">
                <svg className="w-10 h-10 mb-2" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="4" />
                    <path strokeLinecap="round" d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
                </svg>
                <p className="text-sm">No solar data for this date</p>
            </div>
        );
    }

    // Map SOC (0-100%) to the power axis scale for background fill
    const maxPower = Math.max(
        ...historical.map(d => (d.powerdc1 || 0) + (d.powerdc2 || 0)),
        ...historical.map(d => Math.abs(d.feedinpower || 0)),
        ...historical.map(d => Math.abs(d.acpower || 0)),
        ...forecast.map(d => d.predicted_power || 0),
        1000
    );
    const yMax = Math.ceil(maxPower / 1000) * 1000;

    // Cohesive Tailwind-aligned palette: amber=solar/predicted, emerald=AC/export,
    // sky=battery, rose=consumption. All series visible by default; toggle via legend.
    const chartData = {
        // Don't set `labels`: with a time scale + {x,y} data, an empty labels array
        // makes Chart.js fall back to categorical x and the line breaks into dots
        // when historical is empty (future-date views).
        datasets: [
            {
                label: 'Predicted Solar',
                data: forecast.map(item => ({
                    x: item.time,
                    y: item.predicted_power != null ? item.predicted_power : null,
                })),
                fill: false,
                pointRadius: 0,
                borderColor: 'rgba(251, 191, 36, 0.85)',  // amber-400
                borderWidth: 2,
                borderDash: [6, 3],
                yAxisID: 'y',
                order: 0,
                spanGaps: true,
            },
            {
                label: 'Solar',
                data: historical.map(item => ({
                    x: item.time,
                    y: (item.powerdc1 || 0) + (item.powerdc2 || 0) || null,
                })),
                fill: true,
                pointRadius: 0,
                borderColor: 'rgba(245, 158, 11, 1)',  // amber-500
                backgroundColor: 'rgba(245, 158, 11, 0.25)',
                borderWidth: 2,
                yAxisID: 'y',
                order: 2,
            },
            {
                label: 'Inverter',
                data: historical.map(item => ({
                    x: item.time,
                    y: item.acpower != null ? item.acpower : null,
                })),
                fill: false,
                pointRadius: 0,
                borderColor: 'rgba(16, 185, 129, 0.95)',  // emerald-500
                borderWidth: 1.5,
                yAxisID: 'y',
                order: 6,
            },
            {
                label: 'Export',
                data: historical.map(item => ({
                    x: item.time,
                    y: item.feedinpower != null && item.feedinpower > 0 ? item.feedinpower : null,
                })),
                fill: true,
                pointRadius: 0,
                borderColor: 'rgba(110, 231, 183, 0.9)',  // emerald-300
                backgroundColor: 'rgba(110, 231, 183, 0.45)',
                borderWidth: 0,
                yAxisID: 'y',
                order: 7,
            },
            {
                label: 'Battery (W)',
                data: historical.map(item => ({
                    x: item.time,
                    y: item.batPower != null ? item.batPower : null,
                })),
                fill: false,
                pointRadius: 0,
                borderColor: 'rgba(14, 165, 233, 0.95)',  // sky-500
                borderWidth: 1.5,
                yAxisID: 'y',
                order: 1,
            },
            {
                label: 'Battery SOC',
                data: historical.map(item => ({
                    x: item.time,
                    y: item.soc != null ? (item.soc / 100) * yMax : null,
                })),
                fill: true,
                pointRadius: 0,
                borderColor: 'rgba(186, 230, 253, 0.6)',  // sky-200
                backgroundColor: 'rgba(186, 230, 253, 0.35)',
                borderWidth: 0,
                yAxisID: 'y',
                order: 10,
            },
            {
                label: 'Consumption',
                data: historical.map(item => {
                    const ac = item.acpower;
                    const feed = item.feedinpower;
                    if (ac == null && feed == null) return { x: item.time, y: null };
                    return { x: item.time, y: (ac || 0) - (feed || 0) };
                }),
                fill: false,
                pointRadius: 0,
                borderColor: 'rgba(251, 113, 133, 0.9)',  // rose-400
                borderWidth: 1.25,
                yAxisID: 'y',
                order: 3,
            },
        ],
    };

    const chartOptions = {
        ...baseOptions({
            legendPosition: 'bottom',
            legendPadding: 15,
            label: (context) => {
                let label = context.dataset.label || '';
                if (context.parsed.y !== null) {
                    label += label === 'Battery SOC'
                        ? ': ' + (context.parsed.y / yMax * 100).toFixed(0) + '%'
                        : ': ' + context.parsed.y.toFixed(0) + ' W';
                }
                return label;
            },
            scales: {
                // Pinned to the selected London day, regardless of where data starts/ends.
                x: timeAxis(selectedDate),
                y: valueAxis('W', { ticks: { callback: (value) => value.toLocaleString() } }),
            },
        }),
        spanGaps: 600000,
    };

    return (
        <div style={{ height: '100%' }}>
            <Line data={chartData} options={chartOptions} />
        </div>
    );
};

export default SolarChart;
